/**
 * react-i18next compatibility layer
 * This module provides react-i18next API compatibility for our custom i18n system
 * Components importing from 'react-i18next' will use this adapter
 */

import { useI18n } from '../contexts/I18nContext';
import { LanguageCode } from './i18n';

/**
 * useTranslation hook compatible with react-i18next API
 * Maps to our custom I18nContext system
 */
export function useTranslation() {
  const { t, language, setLanguage } = useI18n();
  
  // Map common language codes to our system
  const langMap: Record<string, LanguageCode> = {
    'en': 'gb',
    'vi': 'vn',
    'zh': 'cn',
    'ja': 'jp',
    'ko': 'kr'
  };
  
  const handleChangeLanguage = (lang: string) => {
    const mappedLang = langMap[lang] || lang;
    if (mappedLang === 'vn' || mappedLang === 'gb' || mappedLang === 'cn' || mappedLang === 'jp' || mappedLang === 'kr') {
      setLanguage(mappedLang);
    }
  };
  
  return {
    t: (key: string, options?: { defaultValue?: string; [key: string]: any }) => {
      // Extract params from options (excluding defaultValue)
      const params: Record<string, string | number> = {};
      if (options) {
        Object.keys(options).forEach(k => {
          if (k !== 'defaultValue') {
            params[k] = options[k];
          }
        });
      }
      
      const result = t(key, params);
      
      // If translation not found and defaultValue provided, use it
      if (result === key && options?.defaultValue) {
        // Replace params in defaultValue if needed
        let defaultValue = options.defaultValue;
        if (params && Object.keys(params).length > 0) {
          defaultValue = defaultValue.replace(/\{\{?(\w+)\}?\}/g, (match, paramKey) => {
            return params[paramKey]?.toString() || match;
          });
        }
        return defaultValue;
      }
      
      return result;
    },
    i18n: {
      language: language,
      changeLanguage: handleChangeLanguage,
      getResourceBundle: (lng: string, ns: string) => {
        // Return empty object, we don't use namespaces
        return {};
      }
    },
    ready: true
  };
}

// Default export for module compatibility
export default {
  useTranslation
};

